import React from "react"
import Relay from "react-relay"
import { Link } from "react-router"
import classnames from "classnames"
import Icon from "../elements/Icon"
import { isKeyClick } from "../../scripts/utils"
import {
  getImageSrcFromResource,
  getImageType,
  getPermalink
} from "../../scripts/resource"
import ResourceAttribution from "../elements/ResourceAttribution"
import ResourceImage from "./ResourceImage"
import AudioView from "./AudioView"
import VideoView from "./VideoView"
import RichTextEditor from "../elements/RichTextEditor"
import { getAccountSettings } from "../../scripts/accountSettings"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"
import ReactSVG from "react-svg"
import { injectIntl } from "react-intl"
import settingsMessages from "../../messages/settings"
import { filterHTML } from "../../scripts/xss"

class InlineResource extends React.Component {
  render() {
    const { resource, editable, editing, description, align, intl } = this.props
    const { description: resourceDescriptionProp } = JSON.parse(
      resource.custom_fields
    )

    const permalink = getPermalink(resource)

    const isEditing = editable && !!editing

    let itemType = "http://schema.org/ImageObject"
    if (resource && resource.type === "audio") {
      itemType = "http://schema.org/AudioObject"
    } else if (resource && resource.type === "video") {
      itemType = "http://schema.org/VideoObject"
    }

    const resourceDescription =
      description ||
      (resource && resourceDescriptionProp ? resourceDescriptionProp.value : "")
    const accountSettings = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const contentType = accountSettings.settings.style.content

    return (
      <figure
        style={getStyles("c-figure")}
        className={classnames(`figure figure--${align}`, {
          "editable-item": editable,
          "is-editing": isEditing,
          "not-editing": !isEditing
        })}
        itemScope
        itemType={itemType}
      >
        {getStyleTag(["editable-item", "editable-item__actions__button"])}

        <Link
          style={getStyles("c-figure__media")}
          to={permalink}
          target="_blank"
        >
          {this._renderMedia()}
        </Link>

        <figcaption
          className="figure__figcaption"
          style={getStyles("c-figure__caption")}
        >
          <div
            className="figure__attribution"
            style={getStyles("c-figure__attribution")}
          >
            {contentType == "default" && (
              <Link to={permalink} className="figure__link" target="_blank">
                <Icon icon="arrow-out" />
              </Link>
            )}
            <ResourceAttribution resource={resource} />
          </div>
          {editable &&
            !isEditing && (
              <div
                className="figure__caption"
                itemProp="caption"
                dangerouslySetInnerHTML={{
                  __html: RichTextEditor.RTEValuetoString(
                    filterHTML(resourceDescription)
                  )
                }}
                style={getStyles("c-figure__attribution")}
              />
            )}
          {editable &&
            isEditing && (
              <div
                className="figure__caption"
                style={getStyles("c-figure__attribution")}
              >
                <RichTextEditor
                  placeholder="Add a description..."
                  onChange={value => {
                    this.props.handleChangeDescription(value)
                  }}
                  value={resourceDescription}
                  rows="2"
                />
              </div>
            )}
        </figcaption>
        {getStyleTag("c-button__svg")}
        {editable &&
          isEditing && (
            <div className="editable-item__image-alignment">
              <h5
                className={classnames("editable-item__image-alignment-title", {
                  "title-lined": contentType == "default"
                })}
                style={getStyles("c-article__media-title")}
              >
                <span
                  className={contentType == "default" && "title-lined__inner"}
                >
                  Alignment
                </span>
              </h5>
              <ul>
                <li>
                  <button
                    onClick={e => this._handleAlign(e, "inline")}
                    className={classnames(
                      "editable-item__image-alignment-option",
                      {
                        button: contentType == "default",
                        "state-selected": align === "inline",
                        "c-button__svg": contentType == "type1"
                      }
                    )}
                  >
                    <div style={getStyles(["c-button__icon", "c-button__svg"])}>
                      {contentType == "type1" ? (
                        <ReactSVG src="../../images/align-inline.svg" />
                      ) : (
                        <Icon icon="image-alignment-inline" />
                      )}
                    </div>
                    <div className="button__text">Inline</div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={e => this._handleAlign(e, "full")}
                    className={classnames(
                      "editable-item__image-alignment-option",
                      {
                        button: contentType == "default",
                        "state-selected": align === "full",
                        "c-button__svg": contentType == "type1"
                      }
                    )}
                  >
                    <div style={getStyles(["c-button__icon", "c-button__svg"])}>
                      {contentType == "type1" ? (
                        <ReactSVG
                          className="full"
                          src="../../images/align-full.svg"
                        />
                      ) : (
                        <Icon icon="image-alignment-full" />
                      )}
                    </div>
                    <div className="button__text">Full</div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={e => this._handleAlign(e, "left")}
                    className={classnames(
                      "editable-item__image-alignment-option",
                      {
                        button: contentType == "default",
                        "state-selected": align === "left",
                        "c-button__svg": contentType == "type1"
                      }
                    )}
                  >
                    <div style={getStyles(["c-button__icon", "c-button__svg"])}>
                      {contentType == "type1" ? (
                        <ReactSVG src="../../images/align-left.svg" />
                      ) : (
                        <Icon icon="image-alignment-left" />
                      )}
                    </div>
                    <div className="button__text">Left</div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={e => this._handleAlign(e, "right")}
                    className={classnames(
                      "editable-item__image-alignment-option",
                      {
                        button: contentType == "default",
                        "state-selected": align === "right",
                        "c-button__svg": contentType == "type1"
                      }
                    )}
                  >
                    <div style={getStyles(["c-button__icon", "c-button__svg"])}>
                      {contentType == "type1" ? (
                        <ReactSVG src="../../images/align-right.svg" />
                      ) : (
                        <Icon icon="image-alignment-right" />
                      )}
                    </div>
                    <div className="button__text">Right</div>
                  </button>
                </li>
              </ul>
            </div>
          )}

        {getStyleTag("c-button__icon")}
        {editable &&
          contentType == "type1" && (
            <div style={getStyles("actions-column")}>
              {!isEditing && (
                <button
                  type="button"
                  style={getStyles(["c-button__icon", "c-button__col"])}
                  className="c-button__icon"
                  onKeyPress={e => {
                    if (isKeyClick(e)) this._handleResourceEditToggle(e)
                  }}
                  onClick={this._handleResourceEditToggle}
                  title="Edit resource"
                >
                  <ReactSVG src="../../images/edit.svg" />
                </button>
              )}
              {!isEditing && (
                <button
                  type="button"
                  style={getStyles(["c-button__icon", "c-button__col"])}
                  className="c-button__icon"
                  onKeyPress={e => {
                    if (isKeyClick(e)) this._handleResourceRemove(e)
                  }}
                  onClick={this._handleResourceRemove}
                  title="Remove resource"
                >
                  <Icon icon="cross" />
                </button>
              )}
              {isEditing &&
                this.props.handleMoveUp && (
                  <button
                    type="button"
                    style={getStyles(["c-button__icon", "c-button__col"])}
                    className="c-button__icon"
                    onKeyPress={e => {
                      if (isKeyClick(e)) this.props.handleMoveUp()
                    }}
                    onClick={e => this.props.handleMoveUp()}
                    title="Move section upwards"
                  >
                    <ReactSVG src="../../images/sort-ascending.svg" />
                  </button>
                )}
              {isEditing && (
                <button
                  type="button"
                  style={getStyles(["c-button__icon", "c-button__col"])}
                  className="c-button__icon"
                  onKeyPress={e => {
                    if (isKeyClick(e)) this.props.handleMoveDown()
                  }}
                  onClick={e => this.props.handleMoveDown()}
                  title="Move section downwards"
                >
                  <ReactSVG src="../../images/sort-descending.svg" />
                </button>
              )}
              <Link
                to={permalink}
                style={getStyles(["c-button__icon", "c-button__col"])}
                className="c-button__icon figure__link"
                target="_blank"
              >
                <Icon icon="arrow-out" />
              </Link>
            </div>
          )}

        {editable && (
          <div
            className={
              contentType == "default" &&
              "editable-item__actions editable-item__actions--atbottom"
            }
            style={getStyles("actions-row")}
          >
            <button
              type="button"
              style={getStyles("save-btn")}
              className={
                contentType == "default" && "button editable-item__btn--edit"
              }
              onKeyPress={e => {
                if (isKeyClick(e)) this._handleResourceEditToggle(e)
              }}
              onClick={this._handleResourceEditToggle}
              title={isEditing ? "Finish editing section" : "Edit section"}
            >
              {contentType == "type1" ? (
                <span>Save</span>
              ) : isEditing ? (
                <Icon icon="tick" />
              ) : (
                <Icon icon="edit" />
              )}
            </button>
            {isEditing &&
              contentType == "default" &&
              this.props.handleMoveUp && (
                <button
                  type="button"
                  className="button editable-item__btn-secondary"
                  onKeyPress={e => {
                    if (isKeyClick(e)) this.props.handleMoveUp()
                  }}
                  onClick={e => this.props.handleMoveUp()}
                  title="Move section upwards"
                >
                  <Icon icon="list-move-up" />
                </button>
              )}
            {isEditing &&
              contentType == "default" &&
              this.props.handleMoveDown && (
                <button
                  type="button"
                  className="button editable-item__btn-secondary"
                  onKeyPress={e => {
                    if (isKeyClick(e)) this.props.handleMoveDown()
                  }}
                  onClick={e => this.props.handleMoveDown()}
                  title="Move section downwards"
                >
                  <Icon icon="list-move-down" />
                </button>
              )}
            {isEditing && (
              <button
                type="button"
                style={getStyles("cancel-btn")}
                className={
                  contentType == "default" &&
                  "button editable-item__btn--alert editable-item__btn-secondary"
                }
                onKeyPress={e => {
                  if (isKeyClick(e)) this._handleResourceRemove(e)
                }}
                onClick={this._handleResourceRemove}
                title="Remove resource"
              >
                {contentType == "type1" ? (
                  <span>Cancel</span>
                ) : (
                  <Icon icon="cross" />
                )}
              </button>
            )}
          </div>
        )}
      </figure>
    )
  }

  _renderMedia = () => {
    const resource = this.props.resource

    if (!resource) {
      return <div />
    }

    switch (resource.type) {
      case "audio":
        return (
          <div className="figure__audio figure__audio--smallform">
            <AudioView resource={resource} smallForm />
          </div>
        )

      case "video":
        return (
          <div className="figure__video">
            <VideoView resource={resource} />
          </div>
        )

      default:
        return (
          <div className="figure__image">
            <ResourceImage resource={resource} />
          </div>
        )
    }
  }

  _handleResourceRemove = e => {
    e.preventDefault()

    if (this.props.handleResourceRemove) {
      this.props.handleResourceRemove()
    }
  }

  _handleAlign = (e, alignment) => {
    e.preventDefault()

    if (this.props.handleAlign) {
      this.props.handleAlign(alignment)
    }
  }

  _handleResourceEditToggle = e => {
    e.preventDefault()

    if (this.props.handleResourceEditToggle) {
      this.props.handleResourceEditToggle()
    }
  }

  componentDidMount() {
    if (this.props.handleImageSrc) {
      return this.props.handleImageSrc(
        getImageSrcFromResource(
          this.props.resource,
          "medium",
          getImageType(this.props.resource),
          false
        )
      )
    }
  }
}

InlineResource.defaultProps = {
  editable: true,
  align: "inline"
}

export default Relay.createContainer(injectIntl(InlineResource), {
  fragments: {
    resource: () => Relay.QL`
      fragment on Resource {
        id
        type
        title
        custom_fields
        files {
          id
          width
          height
          type
          images {
            medium {
              url
              width
              height
            }
            large {
              url
              width
              height
            }
          }
        }
        ${AudioView.getFragment("resource")}
        ${VideoView.getFragment("resource")}
        ${ResourceImage.getFragment("resource")}
        ${ResourceAttribution.getFragment("resource")}
      }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/InlineResource.js